import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import Logo from "./Logo";

const NAV = [
    { label: "Home", short: "Home", path: "/", id: "home" },
    { label: "Servizi", short: "Servizi", path: "/servizi", id: "servizi" },
    { label: "Bandi & Finanza agevolata", short: "Bandi & Finanza", path: "/bandi-finanza-agevolata", id: "bandi" },
    { label: "Full Remote", short: "Full Remote", path: "/full-remote", id: "remote" },
    { label: "Chi siamo", short: "Chi siamo", path: "/chi-siamo", id: "chi-siamo" },
    { label: "Contatti", short: "Contatti", path: "/contatti", id: "contatti" },
];

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const on = () => setScrolled(window.scrollY > 24);
        on();
        window.addEventListener("scroll", on, { passive: true });
        return () => window.removeEventListener("scroll", on);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    useEffect(() => {
        const l = window.__lenis;
        if (open) {
            document.body.style.overflow = "hidden";
            l && l.stop();
        } else {
            document.body.style.overflow = "";
            l && l.start();
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <>
            <header
                className={
                    "fixed top-0 inset-x-0 z-50 transition-all duration-300 " +
                    (scrolled || open
                        ? "bg-ink/85 backdrop-blur-md border-b border-white/10"
                        : "bg-transparent border-b border-transparent")
                }
            >
                <div className="wrap flex items-center justify-between h-20">
                <Link to="/" data-testid="nav-link-home-logo" aria-label="Service & Business SRLS — Home">
                    <Logo />
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Menu principale">
                    {NAV.map((n) => (
                        <Link
                            key={n.id}
                            to={n.path}
                            data-testid={`nav-link-${n.id}`}
                            aria-label={n.label}
                            className={
                                "text-[13px] font-medium tracking-wide transition-colors duration-200 whitespace-nowrap " +
                                (pathname === n.path ? "text-white" : "text-white/55 hover:text-white")
                            }
                        >
                            {n.short}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/contatti"
                        data-testid="nav-cta-parla-con-noi"
                        className="hidden sm:inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-display text-[12px] font-semibold tracking-[0.08em] text-white transition-all duration-300 hover:-translate-y-0.5"
                        style={{ background: "linear-gradient(120deg,#2FB8EC 0%,#0A5FC2 100%)", boxShadow: "0 8px 26px -10px rgba(30,140,220,0.55)" }}
                    >
                        PARLA CON NOI
                        <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                    <button
                        type="button"
                        data-testid="mobile-menu-toggle"
                        aria-label={open ? "Chiudi menu" : "Apri menu"}
                        aria-expanded={open}
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="lg:hidden fixed inset-0 top-20 bg-ink/[0.985] backdrop-blur-lg z-40 overflow-y-auto"
                        data-testid="mobile-menu"
                    >
                        <div className="wrap pt-10 pb-16">
                            <nav aria-label="Menu mobile" className="flex flex-col">
                                {NAV.map((n, i) => (
                                    <motion.div
                                        key={n.id}
                                        initial={{ opacity: 0, x: -18 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4, delay: 0.05 + i * 0.05 }}
                                    >
                                        <Link
                                            to={n.path}
                                            data-testid={`mobile-nav-link-${n.id}`}
                                            className={
                                                "flex items-center justify-between py-4 border-b border-white/8 font-display text-2xl font-semibold " +
                                                (pathname === n.path ? "text-white" : "text-white/65")
                                            }
                                        >
                                            {n.label}
                                            <ArrowUpRight className="h-5 w-5 text-sky-400" />
                                        </Link>
                                    </motion.div>
                                ))}
                            </nav>
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4, delay: 0.45 }}
                                className="mt-10"
                            >
                                <Link to="/contatti" data-testid="mobile-cta-parla-con-noi" className="btn-primary w-full">
                                    PARLA CON NOI
                                    <ArrowUpRight className="h-4 w-4" />
                                </Link>
                                <p className="font-mono text-[10px] tracking-[0.28em] text-white/40 mt-8 text-center uppercase">
                                    Isernia, Italia — Operativi anche da remoto
                                </p>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
